import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ItemSourceSentenceTranslationValidatorItemsModel from 'models/ItemSourceSentenceTranslationValidatorItemsModel';
import { ItemSourceSentenceTranslationValidatorModel } from 'models/ItemSourceSentenceTranslationValidatorModel';
import { useForceRender } from 'hooks/useForceRender';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import {
  AsyncTaskStatus,
  ItemSourceSentenceTranslationValidatorItemStatus as ItemStatus,
} from 'constants/itemSourceSentenceTranslationValidatorStatus';
import { asyncTaskStatusRenderer } from '../../../CMFW/grid/cellRenderers/asyncTaskStatus';
import { IFields } from 'CMFW/grid/fields';
import { compareAiConfidence } from 'CMFW/grid/comparators/compareAiConfidence';
import ItemSourceSentenceTranslationValidatorSummary from './ItemSourceSentenceTranslationValidatorSummary';
import ItemSourceSentenceTranslationValidatorBulkActions from './ItemSourceSentenceTranslationValidatorBulkActions';
import { itemSourceSentenceTranslationValidatorItemStatusRenderer } from '../../../CMFW/grid/cellRenderers/itemSourceSentenceTranslationValidatorItemStatus';
import PrioritySelector from 'components/PrioritySelector';
import { StyledTargetSourceRenderer } from '../../../CMFW/grid/cellRenderers/StyledTargetSourceRenderer';

let currentFilter: { type: 'status' | 'decision'; value: string } | null = null;

const ItemSourceSentenceTranslationValidatorDetails: React.FC = () => {
  const model = useContext(ModuleContext) as ItemSourceSentenceTranslationValidatorModel;
  const jobId = model.itemId;

  const forceRender = useForceRender();
  const [activeFilter, setActiveFilter] = useState<{ type: 'status' | 'decision'; value: string } | null>(null);
  const [jobName, setJobName] = useState<string>('');
  const [jobPriority, setJobPriority] = useState<number | undefined>(undefined);
  currentFilter = activeFilter;

  const itemsModel = useMemo(() => new ItemSourceSentenceTranslationValidatorItemsModel({ render: forceRender }), [
    forceRender,
  ]);
  useEffect(() => {
    if (jobId > 0) {
      itemsModel.setJobId(jobId);
      setActiveFilter(null);
      // Load job details to get the name and priority
      model.loadItemData(jobId).then((data) => {
        if (data) {
          if (data.name) {
            setJobName(data.name);
          }
          if (data.priority !== undefined) {
            setJobPriority(data.priority);
          }
        }
      });
    }
  }, [jobId, itemsModel, model]);

  useEffect(() => {
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [activeFilter, itemsModel]);

  const handleChangePriority = async (newPriority: number) => {
    await model.changePriority(jobId, newPriority);
    setJobPriority(newPriority);
  };

  const isFilterActive = () => currentFilter !== null;
  const isRowVisible = (node: any) => {
    if (!currentFilter) return true;
    const data = node.data;
    if (!data) return true;
    switch (currentFilter.type) {
      case 'status':
        if (currentFilter.value === 'processing') return data.status === ItemStatus.PENDING;
        if (currentFilter.value === 'review') return data.status === ItemStatus.NEEDS_USER_REVIEW;
        if (currentFilter.value === 'error') {
          return (
            data.async_task_status === AsyncTaskStatus.FAILED || data.async_task_status === AsyncTaskStatus.DEAD_LETTER
          );
        }
        return true;
      case 'decision':
        if (currentFilter.value === 'cannot_modify') {
          return data.ai_result?.decision === 'change' && data.ai_result?.can_modify === false;
        }
        // Always filter by AI decision, not user's selection
        return data.ai_result?.decision === currentFilter.value;
      default:
        return true;
    }
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.itemSourceSentenceTranslationValidatorActions]: 'itemSourceSentenceTranslationValidatorActionRenderer',
      [IFields.itemSourceSentenceTranslationValidatorAiStatus]: asyncTaskStatusRenderer,
      [IFields.itemSourceSentenceTranslationValidatorAiResult]:
        'itemSourceSentenceTranslationValidatorAiResultRenderer',
      [IFields.itemSourceSentenceTranslationValidatorStatus]:
        'itemSourceSentenceTranslationValidatorItemStatusRenderer',
      [IFields.itemSourceSentenceTranslationValidatorItem]: StyledTargetSourceRenderer,
      [IFields.itemSourceSentenceTranslationValidatorSentence]: StyledTargetSourceRenderer,
      [IFields.conceptValidatorItemStatus]: itemSourceSentenceTranslationValidatorItemStatusRenderer,
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
    }),
    [],
  );

  const columnSorters = useMemo(
    () => ({ [IFields.itemSourceSentenceTranslationValidatorAiResult]: compareAiConfidence }),
    [],
  );

  const gridOptions = useMemo(
    () => ({
      getRowStyle: (params: any) => {
        if (params.data) {
          if (
            params.data.async_task_status === AsyncTaskStatus.FAILED ||
            params.data.async_task_status === AsyncTaskStatus.DEAD_LETTER
          ) {
            return { backgroundColor: '#ffebee' };
          }
          if (typeof params.data.status === 'number') {
            if (params.data.status === ItemStatus.PENDING) return { backgroundColor: '#fffef7' };
            if (params.data.status === ItemStatus.NEEDS_USER_REVIEW) return { backgroundColor: '#fff8f0' };
          }
        }
        return undefined;
      },
    }),
    [],
  );

  // Guard render after establishing hooks to satisfy rules-of-hooks
  if (!jobId || jobId === -1) {
    return null;
  }

  return (
    <Container>
      <ValidatorHeader
        id={jobId}
        name={jobName}
        idLabel="Job"
        bulkActions={
          <>
            <ItemSourceSentenceTranslationValidatorBulkActions
              itemsModel={itemsModel}
              model={model}
              onActionComplete={forceRender}
            />
            <PrioritySelector
              currentPriority={jobPriority ?? 1}
              onChangePriority={handleChangePriority}
              isProcessing={itemsModel.gridData?.some((item: any) => item.status === ItemStatus.PENDING) ?? false}
            />
          </>
        }
        itemsModel={itemsModel}
      />

      <ItemSourceSentenceTranslationValidatorSummary
        itemsModel={itemsModel}
        onFilterChange={setActiveFilter}
        activeFilter={activeFilter}
        onClearFilter={() => setActiveFilter(null)}
      />

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid
            cellRenderers={cellRenderers}
            gridOptions={gridOptions}
            isFilterActive={isFilterActive}
            isRowVisible={isRowVisible}
            columnSorters={columnSorters}
          />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={model}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default ItemSourceSentenceTranslationValidatorDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;
const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
